import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccess, pathIsArea } from "@/lib/access";
import { ChartLineUp, Receipt, Package } from "@phosphor-icons/react";

/**
 * Segmented workspace switcher — Sales / Finance / Inventory.
 *
 * Visible only when the signed-in user has access to more than one workspace.
 * Renders a single compact pill where the active workspace is highlighted
 * using its own accent colour, so admins can hop between modules from any
 * page without going back to the area picker.
 */
const AREA_META = {
  sales: {
    label: "Sales",
    path: "/sales/dashboard",
    icon: ChartLineUp,
    activeBg: "bg-blue-600",
    activeText: "text-white",
    hover: "hover:bg-blue-50 hover:text-blue-700",
  },
  invoice: {
    label: "Finance",
    path: "/invoices",
    icon: Receipt,
    activeBg: "bg-rose-600",
    activeText: "text-white",
    hover: "hover:bg-rose-50 hover:text-rose-700",
  },
  inventory: {
    label: "Inventory",
    path: "/inventory",
    icon: Package,
    activeBg: "bg-emerald-600",
    activeText: "text-white",
    hover: "hover:bg-emerald-50 hover:text-emerald-700",
  },
};

const ORDER = ["sales", "invoice", "inventory"];

export default function ModuleSwitcher({ className = "" }) {
  const { areas } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();

  if (!areas || areas.length < 2) return null;

  const visible = ORDER.filter((a) => areas.includes(a));

  return (
    <div
      className={`inline-flex items-center bg-slate-100 border border-slate-200 rounded-full p-1 ${className}`}
      data-testid="module-switcher"
      role="tablist"
      aria-label="Workspace"
    >
      {visible.map((area) => {
        const meta = AREA_META[area];
        const Icon = meta.icon;
        const isActive = pathIsArea(location.pathname, area);
        return (
          <button
            key={area}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => {
              if (!isActive) navigate(meta.path);
            }}
            data-testid={`switch-${area}`}
            className={
              "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold tracking-tight transition " +
              (isActive
                ? `${meta.activeBg} ${meta.activeText} shadow-sm`
                : `text-slate-600 ${meta.hover}`)
            }
          >
            <Icon size={14} weight={isActive ? "bold" : "duotone"} />
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}
